import { useQuery } from '@tanstack/react-query';
import { Descriptions, Drawer, Empty, Typography } from 'antd';
import {
  iamApi as iamHttpApi,
  type IamPolicyGrantIdentityType,
  type IamPolicyGrantRecord,
} from '@/api/iam.api';
import { PolicyPermissionView } from '@/pages/iam/components/PolicyPermissionView';
import { usePolicyPermissionItems } from '@/pages/iam/composables/usePolicyPermissionItems';
import { fmtTime } from '@/utils/utils';

const IDENTITY_TYPE_LABELS: Record<IamPolicyGrantIdentityType, string> = {
  user_group: '用户组',
  user: '用户',
};

interface Props {
  open: boolean;
  grant?: IamPolicyGrantRecord;
  onClose: () => void;
}

function formatAuthorizationTime(timestamp: number): string {
  return fmtTime(new Date(timestamp * 1000).toISOString());
}

export default function GrantDetail({ open, grant, onClose }: Props) {
  const {
    data: detailResponse,
    error,
    isLoading,
  } = useQuery({
    queryKey: [
      'iam',
      'grants',
      'detail',
      grant?.identity_type,
      grant?.identity,
      grant?.policy_id,
    ] as const,
    queryFn: async () => {
      if (!grant) throw new Error('缺少授权记录');
      const response = await iamHttpApi.getPolicyGrant({
        identity: grant.identity,
        identity_type: grant.identity_type,
        policy_id: grant.policy_id,
      });
      if (response.code !== 0) {
        throw new Error(response.message || '获取授权详情失败');
      }
      return response;
    },
    enabled: open && !!grant,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const detail = detailResponse?.data.grant_record;
  const permissionItems = usePolicyPermissionItems({
    actions: detailResponse?.data.permission_summary.actions,
  });

  return (
    <Drawer
      width={960}
      title="授权详情"
      open={open}
      loading={isLoading}
      onClose={onClose}
      destroyOnHidden
    >
      {error ? (
        <Empty description={error instanceof Error ? error.message : '获取授权详情失败'} />
      ) : (
        detail && (
          <div className="iam-grant-detail-body">
            <Descriptions
              column={1}
              bordered
              size="small"
              items={[
                {
                  key: 'principal',
                  label: '授权对象',
                  children: `${detail.identity_name}`,
                },
                {
                  key: 'type',
                  label: '对象类型',
                  children: IDENTITY_TYPE_LABELS[detail.identity_type] || detail.identity_type,
                },
                {
                  key: 'policy',
                  label: '权限策略',
                  children: `${detail.policy_name}`,
                },
                {
                  key: 'time',
                  label: '授权人',
                  children: `${detail.authorizer_name}`,
                },
                {
                  key: 'time',
                  label: '授权时间',
                  children: formatAuthorizationTime(detail.authorization_at),
                },
              ]}
            />
            <div className="iam-grant-detail-rules">
              <Typography.Title level={5}>授权范围</Typography.Title>
              <PolicyPermissionView
                items={permissionItems}
                emptyDescription="暂无授权范围"
                showFilters={false}
              />
            </div>
          </div>
        )
      )}
    </Drawer>
  );
}
